import React, { useEffect, useState } from "react";
import { Col, Container, Row, Button } from "react-bootstrap";
import ErrorResponse from "../components/ErrorResponse";
import LoadingIndicator from "../components/LoadingIndicator";
import FreestyleDeltasSelectionForm from "../components/FreestyleDeltas/FreestyleDeltasSelectionForm";
import FreestyleDeltasTableDisplay from "../components/FreestyleDeltas/FreestyleDeltasTableDisplay";
import { FreestyleDeltasEditPage } from "../components/FreestyleDeltas/FreestyleDeltasEditPage";
import FreestyleDeltasCollectionBreakdownTables from "../components/FreestyleDeltas/FreestyleDeltasCollectionBreakdownTables";
import { axiosInstance } from "../auth/Auth";
import PageDesciption from "../components/PageDesciption";

const FreestyleDeltas = () => {
    const [collections, setCollections] = useState([[], [], [], [], []]);
    const [freestyleDeltasData, setFreestyleDeltasData] = useState([null, null, null, null, null]);
    const [demos, setDemos] = useState([[], [], [], [], []]);
    const [averages, setAverages] = useState([[], [], [], [], []]);
    const [dataLoading, setDataLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);
    const [openEditPage, setOpenEditPage] = useState(false);
    const [editParams, setEditParams] = useState(false);
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [editCollectionData, setEditCollectionData] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        setOpenEditPage(false);
    }, []);

    const handleDataRefresh = () => {
        setRefreshKey(prevKey => prevKey + 1);
    };

    const handleFormSubmit = async index => {
        setDataLoading(true);

        const collectionToAnalyze = collections[index];
        const demoToAnalyze = demos[index];

        try {
            const response = await axiosInstance.get("freestyle_deltas/" + collectionToAnalyze[0]?.collection_id + "/bits/", {
                collection_id: collectionToAnalyze[0]?.collection_id,
                demo_id: demoToAnalyze[0]?.demo_id,
                listener_group: demoToAnalyze[0]?.listener_group,
            });
            setErrorCode(null);
            setFreestyleDeltasData(prev => [...prev.slice(0, index), response.data, ...prev.slice(index + 1)]);
        } catch (error) {
            setFreestyleDeltasData(prev => [...prev.slice(0, index), null, ...prev.slice(index + 1)]);
            setErrorCode(error?.response?.status ?? 500);
        }
        setDataLoading(false);
    };

    const handleAnalyzeAll = async () => {
        for (let i = 0; i < collections.length; i++) {
            if (collections[i].length > 0 && demos[i].length > 0) {
                await handleFormSubmit(i);
            }
        }
    };

    const handleRemove = indexToRemove => {
        setCollections(prev => {
            const updatedCollections = [...prev.slice(0, indexToRemove), ...prev.slice(indexToRemove + 1), []];
            return updatedCollections;
        });

        setDemos(prev => {
            const updatedDemos = [...prev.slice(0, indexToRemove), ...prev.slice(indexToRemove + 1), []];
            return updatedDemos;
        });

        setFreestyleDeltasData(prev => {
            const updatedFreestyleDeltasData = [...prev.slice(0, indexToRemove), ...prev.slice(indexToRemove + 1), null];
            return updatedFreestyleDeltasData;
        });
        setSelectedCollections(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    return (
        <Container fluid className="p-4">
            {!openEditPage ? (
                <>
                    <Row>
                        <Col className="bg-white p-4">
                            <h1 className="text-uppercase">Freestyle Deltas</h1>
                            <Col xs={12} xl={12} className="text-center text-xl-start align-self-end mt-1">
                                <Button
                                    type="submit"
                                    onClick={() => {
                                        setOpenEditPage(true);
                                        handleDataRefresh();
                                    }}
                                    className="btn-ra-yellow text-uppercase px-4 py-1 mb-xl-1">
                                    Add/Edit Breaks
                                </Button>
                            </Col>
                            {collections.map(
                                (_, index) =>
                                    (index === 0 || (collections[index - 1] && collections[index - 1].length > 0)) && (
                                        <FreestyleDeltasSelectionForm
                                            key={index}
                                            freestyleDeltasData={freestyleDeltasData[index]}
                                            setFreestyleDeltasData={data =>
                                                setFreestyleDeltasData(prev => [...prev.slice(0, index), data, ...prev.slice(index + 1)])
                                            }
                                            demo={demos[index]}
                                            setDemo={demo => setDemos(prev => [...prev.slice(0, index), demo, ...prev.slice(index + 1)])}
                                            collection={collections[index]}
                                            setCollection={collection =>
                                                setCollections(prev => [...prev.slice(0, index), collection, ...prev.slice(index + 1)])
                                            }
                                            displayLabels={index === 0}
                                            selectedCollections={selectedCollections}
                                            setSelectedCollections={setSelectedCollections}
                                            index={index}
                                            handleFormSubmit={() => handleFormSubmit(index)}
                                            handleRemove={() => handleRemove(index)}
                                            refreshKey={refreshKey} // Passing the refresh function
                                        />
                                    )
                            )}
                            <Col xs={12} xl={12} className="text-center text-xl-end align-self-end">
                                <Button
                                    type="submit"
                                    onClick={handleAnalyzeAll}
                                    className="btn-ra-yellow text-uppercase px-5 py-1 mb-xl-1"
                                    disabled={
                                        dataLoading ||
                                        collections.filter(collection => collection.length > 0).length !== demos.filter(demo => demo.length > 0).length
                                    }>
                                    Analyze All
                                </Button>
                            </Col>
                        </Col>
                    </Row>
                    <hr className="m-2"></hr>
                    <Row>
                        <Col className="bg-white p-4">
                            {!dataLoading && !errorCode && freestyleDeltasData.every(element => element === null) && (
                                <PageDesciption
                                    title={"Freestyle Deltas"}
                                    whatList={[
                                        "Create your own collection or breaks, benchmarks, or topics",
                                        "Label the breaks to easily see what is working and what isnt",
                                        "Positive deltas show longer listening than usual, negative deltas show early tune out",
                                    ]}
                                    knowList={[
                                        { text: "You cannot analyze bits that are on a day with corrupted streaming data" },
                                        {
                                            text: "You can do analysis on holidays, but be mindful of the difference in listening (wake up late, no work, etc.)",
                                        },
                                        { text: "Listener groups are only available after 1 full week" },
                                    ]}
                                    videoSrc="https://www.youtube.com/embed/S_GQTLWx8ag?si=Q0OOf-WtF_clyeeB"
                                />
                            )}
                            {!dataLoading && errorCode && <ErrorResponse errorCode={errorCode} />}
                            {dataLoading ? (
                                <span colSpan={12}>
                                    <LoadingIndicator />
                                </span>
                            ) : (
                                <>
                                    <Row>
                                        {freestyleDeltasData.map(
                                            (data, index) =>
                                                data && (
                                                    <FreestyleDeltasCollectionBreakdownTables
                                                        key={`breakdownTable${index + 1}`}
                                                        averages={averages[index]}
                                                        collection={collections[index]}
                                                    />
                                                )
                                        )}
                                    </Row>

                                    {freestyleDeltasData.map(
                                        (data, index) =>
                                            data && (
                                                <FreestyleDeltasTableDisplay
                                                    key={`table${index + 1}`}
                                                    freestyleDeltasData={data}
                                                    setFreestyleDeltasData={newData =>
                                                        setFreestyleDeltasData(prev => [...prev.slice(0, index), newData, ...prev.slice(index + 1)])
                                                    }
                                                    demo={demos[index]}
                                                    collection={collections[index]}
                                                    setCollection={newCollection =>
                                                        setCollections(prevCollections => [
                                                            ...prevCollections.slice(0, index),
                                                            newCollection,
                                                            ...prevCollections.slice(index + 1),
                                                        ])
                                                    }
                                                    setCollections={setCollections}
                                                    openEditPage={openEditPage}
                                                    setOpenEditPage={setOpenEditPage}
                                                    setAverages={avg => setAverages(prev => [...prev.slice(0, index), avg, ...prev.slice(index + 1)])}
                                                    showStack={true}
                                                    setEditCollectionData={setEditCollectionData}
                                                    handleDataRefresh={handleDataRefresh}
                                                />
                                            )
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                </>
            ) : (
                <FreestyleDeltasEditPage
                    openEditPage={openEditPage}
                    setOpenEditPage={setOpenEditPage}
                    editParams={editParams}
                    editCollectionData={editCollectionData}
                    setEditCollectionData={setEditCollectionData}
                    handleDataRefresh={handleDataRefresh}
                    refreshKey={refreshKey}
                />
            )}
        </Container>
    );
};

export default FreestyleDeltas;
